import { api, HttpError, type BetaUser } from './api';
import { forgetReturnForOtherSubject, forgetReturnSubject, rememberReturnSubject } from './return-to';

/**
 * Who is using the app on this device right now (ticket 12).
 *
 * Device drafts belong to the account they were captured under, so every
 * screen that lists or sends them needs a confirmed answer to "who is signed
 * in". In beta that is `/api/auth/me`; a personal instance has one learner,
 * the `'personal'` owner. Until the server has answered, nothing is known and
 * no draft is shown or sent.
 *
 * Offline cold start: the last identity the server confirmed on this device
 * is remembered, and used *provisionally* — its drafts are listed and a new
 * capture can be saved under it, but nothing is sent until the server has
 * confirmed the session again. An explicit sign-out or an expired session
 * forgets it.
 */

export const PERSONAL_OWNER = 'personal';

export interface Identity {
  /** Beta: the account id. Personal mode: `'personal'`. */
  owner: string;
  target: string;
  explain: string;
  beta: boolean;
}

export type Session =
  | { status: 'unknown' }
  | { status: 'signed-out' }
  | ({ status: 'known'; provisional: boolean } & Identity);

const CACHE_KEY = 'li-session';

let current: Session = { status: 'unknown' };
const listeners = new Set<(s: Session) => void>();

export function session(): Session {
  return current;
}

/** The owner whose drafts may be shown, or null while nobody is known. */
export function currentOwner(): string | null {
  return current.status === 'known' ? current.owner : null;
}

/** True when the server itself has confirmed who is signed in (not the offline guess). */
export function confirmed(): boolean {
  return current.status === 'known' && !current.provisional;
}

export function onSession(fn: (s: Session) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function same(a: Session, b: Session): boolean {
  if (a.status !== b.status) return false;
  if (a.status !== 'known' || b.status !== 'known') return true;
  return a.owner === b.owner && a.provisional === b.provisional && a.target === b.target && a.explain === b.explain && a.beta === b.beta;
}

function set(next: Session) {
  if (same(current, next)) return;
  current = next;
  for (const fn of listeners) {
    try {
      fn(current);
    } catch { /* a broken view must not break the session */ }
  }
}

function remember(id: Identity) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(id));
  } catch { /* private mode: no offline identity, drafts wait for the server */ }
}

function forget() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch { /* ignore */ }
}

function remembered(): Identity | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Partial<Identity>;
    if (typeof v.owner !== 'string' || !v.owner) return undefined;
    return { owner: v.owner, target: String(v.target ?? ''), explain: String(v.explain ?? ''), beta: v.beta === true };
  } catch {
    return undefined;
  }
}

function known(id: Identity) {
  remember(id);
  set({ status: 'known', provisional: false, ...id });
}

/** Signed in (sign-in, invitation, recovery): the account the server just answered with. */
export function adoptUser(user: BetaUser) {
  // A return path belongs to the account that recorded it. Clear another account's or an unbound
  // legacy intent before any sign-in or recovery flow can consume it.
  forgetReturnForOtherSubject(user.id);
  rememberReturnSubject(user.id);
  known({ owner: user.id, target: user.target, explain: user.explain, beta: true });
}

/** The session is over on this device (explicit sign-out or expiry). Drafts stay, hidden. */
export function endSession() {
  forget();
  forgetReturnSubject();
  set({ status: 'signed-out' });
}

let resolving: Promise<Session> | null = null;

/**
 * Ask the server who is signed in. A network failure keeps what we had (or
 * falls back to the remembered identity, provisionally); `user: null` (or a
 * 401) means nobody.
 */
export function resolveSession(): Promise<Session> {
  if (resolving) return resolving;
  resolving = (async () => {
    try {
      const health = await api.health();
      if (!health.beta) {
        known({ owner: PERSONAL_OWNER, target: health.target ?? '', explain: health.explain ?? '', beta: false });
      } else {
        const { user } = await api.me();
        if (user) adoptUser(user);
        else endSession();
      }
    } catch (e) {
      if (e instanceof HttpError && e.status === 401) {
        endSession();
      } else if (current.status === 'unknown') {
        // Offline (or the server hiccuped): the last confirmed identity, provisionally.
        const id = remembered();
        if (id) set({ status: 'known', provisional: true, ...id });
      }
    } finally {
      resolving = null;
    }
    return current;
  })();
  return resolving;
}

let wired = false;

/** Resolve once at start, and forget the account whenever the server says the session is gone. */
export function ensureSession() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener('li-session-lost', () => {
    if (current.status === 'known' && !current.beta) return;
    endSession();
  });
  void resolveSession();
}
